import React from 'react';
import Link from 'next/link';
import parseISO from 'date-fns/parseISO';
import format from 'date-fns/format';
import nl from 'date-fns/locale/nl';
import Time from './time';

import { Muted, Text } from './text';
import { LinkButton } from './link-button';
import { IMatch } from '../models/match';
import { MatchStatus } from '../models/match-status';

interface IMatchWithoutTitleAndSlug extends Omit<IMatch, 'title' | 'slug'> {}

interface IMatchCardProps extends IMatchWithoutTitleAndSlug {
	snug?: boolean;
	className?: string;
}

export function MatchCard({
	channel,
	snug,
	venue,
	date,
	time,
	homeContestant,
	awayContestant,
	status,
	scores,
	className,
}: IMatchCardProps) {
	const snugToppadding = snug ? 'xl:py-8' : 'xl:py-12';
	const snugInnerPadding = snug ? 'lg:px-4' : 'lg:px-6';

	return (
		<div
			className={`relative w-full h-full rounded-md bg-white border p-3 sm:p-8 lg:p-2 lg:py-8 ${snugToppadding} ${
				className ?? ''
			}`}
			onClick={(e) => e.stopPropagation()}
		>
			{channel && (
				<div className="w-16 absolute top-0 left-1/2 -translate-x-1/2">
					<img src={`/images/channel_${channel}.png`} alt="ESPN" />
				</div>
			)}

			<div className={`mx-auto max-w-7xl px-2 sm:px-6 ${snugInnerPadding} xl:px-8`}>
				<div className="grid grid-cols-3 gap-2 sm:gap-8 text-center">
					<div className="mx-auto flex max-w-xs flex-col gap-y-4 justify-center">
						{homeContestant.contestantLogo ? (
							<Link href={`/teams/${homeContestant.contestantClubSlug}`}>
								<img
									className="max-h-16 lg:max-h-24 w-full object-contain"
									src={homeContestant.contestantLogo}
									alt={homeContestant.contestantClubName}
									width={124}
									height={124}
								/>
							</Link>
						) : (
							<Muted className="text-base font-inter font-bold leading-tight">
								{homeContestant.contestantClubName}
							</Muted>
						)}

						{!snug && (
							<Link href={`/teams/${homeContestant.contestantClubSlug}`}>
								<Muted className="text-base font-inter font-bold leading-tight">
									{homeContestant.contestantClubName}
								</Muted>
							</Link>
						)}
						{status === MatchStatus.Fixture && (
							<LinkButton size="small" href={homeContestant.contestantTicketURL} target="_blank">
								Tickets
							</LinkButton>
						)}
					</div>
					<div
						className={`mx-auto flex max-w-xs flex-col gap-y-4 justify-center lg:mt-4 ${
							snug ? 'sm:justify-start' : ''
						}`}
					>
						<div className="max-h-24 h-24 w-full flex flex-col justify-center items-center text-center relative">
							{status === MatchStatus.Playing || status === MatchStatus.Played ? (
								<>
									<Muted className="mb-1 font-inter">
										<time className="text-base lg:text-2xl">
											{format(parseISO(date), 'd MMM yyyy', { locale: nl })}
										</time>
									</Muted>
									<Text className="inline-block font-inter font-bold text-3xl leading-none tracking-tight text-black-500 sm:text-4xl lg:text-5xl xl:text-6xl">
										<span>{scores?.home ?? 0}</span> - <span>{scores?.away ?? 0}</span>
									</Text>
								</>
							) : (
								<>
									<Muted className="mb-1 font-inter">
										<time className="text-base lg:text-2xl">
											{format(parseISO(date), 'd MMM yyyy', { locale: nl })}
										</time>
									</Muted>
									<Text className="inline-block font-inter font-bold text-3xl leading-none tracking-tight text-black-500 sm:text-4xl lg:text-5xl xl:text-6xl">
										<Time timeString={time} />
									</Text>
								</>
							)}
						</div>
						<Muted className="hidden sm:block text-base font-inter font-bold leading-tight">{venue}</Muted>
					</div>
					<div className="mx-auto flex max-w-xs flex-col gap-y-4 justify-center">
						{awayContestant.contestantLogo ? (
							<Link href={`/teams/${awayContestant.contestantClubSlug}`}>
								<img
									className="max-h-16 lg:max-h-24 w-full object-contain"
									src={awayContestant.contestantLogo}
									alt={awayContestant.contestantClubName}
									width={124}
									height={124}
								/>
							</Link>
						) : (
							<Muted className="text-base font-inter font-bold leading-tight">
								{awayContestant.contestantClubName}
							</Muted>
						)}
						{!snug && (
							<Link href={`/teams/${awayContestant.contestantClubSlug}`}>
								<Muted className="text-base font-inter font-bold leading-tight">
									{awayContestant.contestantClubName}
								</Muted>
							</Link>
						)}
						{status === MatchStatus.Fixture && (
							<LinkButton size="small" href={awayContestant.contestantTicketURL} target="_blank">
								Tickets
							</LinkButton>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
